import { camelizeKeys } from 'humps';
import { useEffect, useRef } from 'react';
import { AppState } from 'react-native';
import { CheckSeatsResponseProps, SeatProps } from 'src/common/api/response';
import { formatDatePayload } from 'src/common/utils/DateHelper';
import EchoSocket from 'src/common/utils/Socket';
import { useReselectModalContext } from 'src/components';
import { useTripInfoState } from '../states/useTripInfoState';
import { useUserInfoState } from '../states/useUserInfoState';
import { useCheckSeatMutation } from '../useCheckSeatMutation';

export const useGlobalSeatSelection = () => {
  const { userInfo } = useUserInfoState();
  const {
    departureSeatChannel,
    returnSeatChannel,
    departureTripInfo,
    returnTripInfo,
    searchInfo,
    setDepartureSeatsSelected,
    setReturnSeatsSelected,
    passengerInfo,
  } = useTripInfoState();
  const { showModal } = useReselectModalContext();
  const { mutateAsync: checkSeatMutateAsync } = useCheckSeatMutation();
  const appState = useRef(AppState.currentState);

  const departureSeatsRef = useRef(passengerInfo.passengerDepartureSeats);
  const returnSeatsRef = useRef(passengerInfo.passengerReturnSeats);
  const departureTripInfoRef = useRef(departureTripInfo);
  const returnTripInfoRef = useRef(returnTripInfo);

  useEffect(() => {
    departureSeatsRef.current = passengerInfo.passengerDepartureSeats;
  }, [passengerInfo.passengerDepartureSeats]);

  useEffect(() => {
    returnSeatsRef.current = passengerInfo.passengerReturnSeats;
  }, [passengerInfo.passengerReturnSeats]);

  useEffect(() => {
    console.log({ departureTripInfodepartureTripInfo: departureTripInfo });
    departureTripInfoRef.current = departureTripInfo;
  }, [departureTripInfo]);

  useEffect(() => {
    returnTripInfoRef.current = returnTripInfo;
  }, [returnTripInfo]);

  const handleCheckAppState = async (tripId: string, selectedSeatsCheck: SeatProps[], isReturnTrip: boolean) => {
    console.log({ selectedSeatsCheck });
    const selectedSeats = selectedSeatsCheck.map(seat => ({
      seatId: `${seat.id}`,
      transitId: seat.transitId,
    }));
    const response = await checkSeatMutateAsync({
      tripId,
      date: formatDatePayload(searchInfo?.departureDate),
      selectedSeats,
    }).then(res => res.data.data);

    if (response.length === 0) return;

    const { filterSeatsSelectedAlreadyBusy, seatsSelectedAlreadyBusy } = handleCheckSeats({
      data: {
        tripId,
        fromLocation: searchInfo?.fromLocation,
        toLocation: searchInfo?.toLocation,
      },
      // remove map after backend done
      // busySeats: response.map(res => ({ ...res, seatId: res.id, seatLabel: res.label })),
      busySeats: response,
      isReturnTrip,
      seats: departureSeatsRef.current,
    });

    if (seatsSelectedAlreadyBusy.length === 0) return;

    if (isReturnTrip) {
      setReturnSeatsSelected(filterSeatsSelectedAlreadyBusy);
    } else {
      setDepartureSeatsSelected(filterSeatsSelectedAlreadyBusy);
    }
  };

  useEffect(() => {
    if (!userInfo) return undefined;
    let unsubscription: any;
    let subscription: any;

    if (departureSeatChannel) {
      unsubscription = EchoSocket.seatAlreadySelected({
        channel: departureSeatChannel,
        onSeatBusy: (data: any) => {
          console.log('depart socket onSeatBusy');
          if (appState.current !== 'active') {
            console.log({ appState: appState.current });
            return;
          }
          handleDeartureSeat(data);
        },
        onSeatBooked: data => {
          console.log('depart socket onSeatBooked');
          if (appState.current !== 'active') {
            console.log({ appState: appState.current });
            return;
          }
          handleDeartureSeat(data);
        },
        onSeatCancelled: data => {
          // console.log('depart socket onSeatCancelled');
          // if (appState.current !== 'active') {
          //   console.log({ appState: appState.current });
          //   return;
          // }
          // handleDeartureSeat(data);
        },
      });

      subscription = AppState.addEventListener('change', nextAppState => {
        if (appState.current.match(/inactive|background/) && nextAppState === 'active') {
          // call api to fetch if seat is busy
          console.log({ departureTripInfoRef: departureTripInfoRef.current });
          console.log({ departureSeatsRef: departureSeatsRef.current });
          if (departureSeatsRef.current.length > 0) {
            handleCheckAppState(departureTripInfoRef.current?.tripId || '', departureSeatsRef.current, false);
          }
          return;
        }
        appState.current = nextAppState;
      });
    }

    return () => {
      if (unsubscription) unsubscription();
      if (subscription) subscription.remove();
    };
  }, [userInfo, departureSeatChannel]);

  useEffect(() => {
    if (!userInfo) return undefined;
    let unsubscription: any;
    let subscription: any;

    if (returnSeatChannel) {
      unsubscription = EchoSocket.seatAlreadySelected({
        channel: returnSeatChannel,
        onSeatBusy: (data: any) => {
          console.log('return socket seatAlreadySelected');
          if (appState.current !== 'active') {
            console.log({ appState: appState.current });
            return;
          }
          handleReturnSeat(data);
        },
        onSeatBooked: data => {
          console.log('depart socket onSeatBooked');
          if (appState.current !== 'active') {
            console.log({ appState: appState.current });
            return;
          }
          handleReturnSeat(data);
        },
        onSeatCancelled: data => {
          // console.log('depart socket onSeatCancelled');
          // if (appState.current !== 'active') {
          //   console.log({ appState: appState.current });
          //   return;
          // }
          // handleReturnSeat(data);
        },
        // onSeatBooked: handleReturnSeat,
      });
      subscription = AppState.addEventListener('change', nextAppState => {
        if (appState.current.match(/inactive|background/) && nextAppState === 'active') {
          handleCheckAppState(returnTripInfoRef.current?.tripId || '', returnSeatsRef.current, true);
          return;
        }
        appState.current = nextAppState;
      });
    }

    return () => {
      if (unsubscription) unsubscription();
      if (subscription) subscription.remove();
    };
  }, [userInfo, returnSeatChannel]);

  const handleCheckSeats = ({ data, busySeats, isReturnTrip, seats }: LoopCheckSeatsProps) => {
    console.log({ data, busySeats, isReturnTrip, seats });
    const seatsSelectedAlreadyBusy = [];
    const filterSeatsSelectedAlreadyBusy = [];
    for (let i = 0; i < seats.length; i += 1) {
      for (let j = 0; j < busySeats.length; j += 1) {
        if (seats[i].id === busySeats[j].seatId) {
          // have seat that current user selected
          seatsSelectedAlreadyBusy.push(seats[i]);
        }
      }
    }
    if (seatsSelectedAlreadyBusy.length === 0) {
      // seats are not owned by this user
      return {
        filterSeatsSelectedAlreadyBusy: [],
        seatsSelectedAlreadyBusy: [],
      };
    }

    for (let i = 0; i < seats.length; i += 1) {
      if (seatsSelectedAlreadyBusy.findIndex(busy => busy.id === seats[i].id) === -1) {
        filterSeatsSelectedAlreadyBusy.push(seats[i]);
      }
    }
    console.log({ filterSeatsSelectedAlreadyBusy, seatsSelectedAlreadyBusy });
    return { filterSeatsSelectedAlreadyBusy, seatsSelectedAlreadyBusy };
    // setDepartureSeatsSelected(filterSeatsSelectedAlreadyBusy);
  };

  const handleDeartureSeat = (data: any) => {
    const camelData = camelizeKeys(data);
    console.log('departure seat enter', camelData.busySeats, departureSeatsRef.current);

    console.log({ userId: camelData.userId, id: userInfo?.id });
    if (window?.nham24) window?.nham24?.request('consolelog', { userId: camelData.userId, id: userInfo?.id });

    if (`${camelData.userId}` === `${userInfo?.id}`) return;

    const { filterSeatsSelectedAlreadyBusy, seatsSelectedAlreadyBusy } = handleCheckSeats({
      data: camelData,
      busySeats: camelData.busySeats,
      isReturnTrip: false,
      seats: departureSeatsRef.current,
    });

    const totalSeatSelectedCount = departureSeatsRef.current.length;

    if (seatsSelectedAlreadyBusy.length > 0) {
      setDepartureSeatsSelected(filterSeatsSelectedAlreadyBusy);
      showModal({
        ...camelData,
        seatsSelectedAlreadyBusy,
        totalSeatSelectedCount,
        isReturnTrip: false,
      } as any);
    }
  };

  const handleReturnSeat = (data: any) => {
    console.log('return seat enter');
    const camelData = camelizeKeys(data);
    if (`${camelData.userId}` === `${userInfo?.id}`) return;

    const { filterSeatsSelectedAlreadyBusy, seatsSelectedAlreadyBusy } = handleCheckSeats({
      data: camelData,
      busySeats: camelData.busySeats,
      isReturnTrip: false,
      seats: returnSeatsRef.current,
    });

    const totalSeatSelectedCount = departureSeatsRef.current.length;

    if (seatsSelectedAlreadyBusy.length > 0) {
      setReturnSeatsSelected(filterSeatsSelectedAlreadyBusy);
      showModal({
        ...camelData,
        seatsSelectedAlreadyBusy,
        totalSeatSelectedCount,
        isReturnTrip: true,
      } as any);
    }
  };
};

interface LoopCheckSeatsProps {
  data?: any;
  busySeats: CheckSeatsResponseProps[];
  isReturnTrip: boolean;
  seats: SeatProps[];
}
